import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { isRecoverableAuthError } from '../utils/auth0Errors';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';

export function useRecentStocks(options = {}) {
  const { fetchOnMount = true } = options;
  const shouldFetchOnMountRef = useRef(fetchOnMount);
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [recents, setRecents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRecents = useCallback(async () => {
    if (!isAuthenticated) {
      setRecents([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently({
        detailedResponse: false,
      }).catch((tokenError) => {
        if (isRecoverableAuthError(tokenError)) {
          return null;
        }
        throw tokenError;
      });
      
      if (!token) {
        setRecents([]);
        return;
      }
      
      const res = await fetch(`${apiBaseUrl}/recent-stocks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load recents (${res.status})`);
      const json = await res.json();
      setRecents(json.recents || []);
    } catch (err) {
      if (isRecoverableAuthError(err)) {
        setRecents([]);
        return;
      }

      console.error('Error fetching recent stocks', err);
      setError('Could not load recent stocks.');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  const addRecent = useCallback(
    async (symbol) => {
      if (!isAuthenticated || !symbol) return;
      try {
        const token = await getAccessTokenSilently({
          detailedResponse: false,
        }).catch((tokenError) => {
          if (isRecoverableAuthError(tokenError)) {
            return null;
          }
          throw tokenError;
        });
        
        if (!token) {
          return;
        }
        
        const res = await fetch(`${apiBaseUrl}/recent-stocks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ symbol }),
        });
        if (!res.ok) throw new Error(`Failed to add recent (${res.status})`);
        const json = await res.json();
        const recent = json.recent;
        if (recent) {
          setRecents((prev) => {
            const filtered = prev.filter((r) => r.symbol !== recent.symbol);
            return [{ ...recent }, ...filtered].slice(0, 25);
          });
        } else {
          fetchRecents();
        }
      } catch (err) {
        if (isRecoverableAuthError(err)) {
          return;
        }

        console.error('Error adding recent stock', err);
        setError('Could not save recent stock.');
      }
    },
    [fetchRecents, getAccessTokenSilently, isAuthenticated]
  );

  useEffect(() => {
    if (!shouldFetchOnMountRef.current) return;
    fetchRecents();
  }, [fetchRecents]);

  return { recents, loading, error, refresh: fetchRecents, addRecent };
}
